import React, { useEffect } from "react";

import GoogleAuth from "@/editor/components/account/GoogleAuth";
import Avatar from "@/editor/components/common/designSystem/Avatar";
import Input from "@/editor/components/common/designSystem/Input";
import LabeledControl from "@/editor/components/common/designSystem/LabeledControl";
import { useProfileForm } from "@/editor/hooks/useProfileForm";
import { errorToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import Description from "@replo/design-system/components/label/Description";
import PageHeader from "@replo/design-system/components/PageHeader";
import Separator from "@replo/design-system/components/Separator";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { CheckCircle2 } from "lucide-react";

import { LoadedSettings, SettingsLayout } from "./settings/SettingsCard";

const Profile = () => {
  const {
    user,
    registerName,
    handleNameSubmit,
    nameErrors,
    isUpdatingName,
    hasNameChanged,
    registerPassword,
    handlePasswordSubmit,
    passwordErrors,
    isUpdatingPassword,
    canUpdatePassword,
    googleErrorMessage,
    handleGoogleConnect,
  } = useProfileForm();

  useEffect(() => {
    if (googleErrorMessage) {
      errorToast("Verification Error", googleErrorMessage);
    }
  }, [googleErrorMessage]);

  const sections = [
    {
      component: (
        <div className="flex flex-col gap-4 w-full">
          <div className="flex items-center gap-2">
            <Avatar
              size="lg"
              name={user?.name}
              imageClassName={twMerge(
                "bg-info-soft text-primary text-3xl font-semibold",
              )}
            />
            <div className="flex flex-col">
              <div className="text-base font-medium">{user?.name}</div>
              <Description description={user?.email} size="base" />
            </div>
          </div>
          <Separator />
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => void handleNameSubmit(e)}
          >
            <div className="flex w-full gap-2">
              <LabeledControl
                label="First Name"
                containerClassName="flex-1"
                error={nameErrors.firstName?.message}
              >
                <Input
                  placeholder="Enter your first name"
                  size="base"
                  {...registerName("firstName")}
                />
              </LabeledControl>
              <LabeledControl
                label="Last Name"
                containerClassName="flex-1"
                error={nameErrors.lastName?.message}
              >
                <Input
                  placeholder="Enter your last name"
                  size="base"
                  {...registerName("lastName")}
                />
              </LabeledControl>
            </div>
            <div className="flex flex-col gap-2">
              <LabeledControl
                label="Email"
                description="To change your email, please reach out to support@replo.app."
                containerClassName="flex-1"
              >
                <Input
                  placeholder="Enter your email"
                  size="base"
                  isDisabled
                  value={user?.email ?? ""}
                  endEnhancer={() => (
                    <>
                      {user?.verifiedAt && (
                        <Tag
                          variant="success"
                          size="sm"
                          startEnhancer={
                            <CheckCircle2 className="text-success" size={14} />
                          }
                          tooltipText="Email verified"
                        >
                          Verified
                        </Tag>
                      )}
                    </>
                  )}
                />
              </LabeledControl>
              <div className="flex justify-start">
                {!user?.verifiedAt && (
                  <GoogleAuth
                    handleGoogleCredentials={handleGoogleConnect}
                    className="justify-start"
                    width={230}
                  />
                )}
              </div>
            </div>
            <div className="flex w-full justify-end">
              <Button
                variant="primary"
                size="base"
                type="submit"
                isLoading={isUpdatingName}
                disabled={isUpdatingName || !hasNameChanged}
              >
                Update Profile
              </Button>
            </div>
          </form>
        </div>
      ),
    },
    {
      title: "Update Password",
      component: (
        <form
          className="flex flex-col gap-4 w-full"
          onSubmit={(e) => void handlePasswordSubmit(e)}
        >
          <div className="flex w-full gap-2">
            <LabeledControl
              label="Current Password"
              containerClassName="flex-1"
              error={passwordErrors.password?.message}
            >
              <Input
                placeholder="••••••••••••"
                size="base"
                type="password"
                validityState={
                  passwordErrors.password?.message ? "invalid" : undefined
                }
                {...registerPassword("password")}
              />
            </LabeledControl>
            <LabeledControl
              label="New Password"
              containerClassName="flex-1"
              error={passwordErrors.newPassword?.message}
            >
              <Input
                placeholder="••••••••••••"
                size="base"
                type="password"
                validityState={
                  passwordErrors.newPassword?.message ? "invalid" : undefined
                }
                {...registerPassword("newPassword")}
              />
            </LabeledControl>
          </div>
          <div className="flex w-full justify-end">
            <Button
              variant="primary"
              size="base"
              type="submit"
              isLoading={isUpdatingPassword}
              disabled={!canUpdatePassword}
            >
              Update Password
            </Button>
          </div>
        </form>
      ),
    },
  ];

  return (
    <div className="flex flex-col max-w-[800px] w-full gap-4">
      <PageHeader title="Account Settings" />
      <SettingsLayout maxWidth="max-w-[800px]">
        <LoadedSettings sections={sections} />
      </SettingsLayout>
    </div>
  );
};

export default Profile;
